import { parseApiErrorPayload, parseApiErrorStatus } from '@/lib/api-errors'
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  CLASS_PLACEMENT_KEYS,
  classPlaceCount,
  formatPlaceCount,
  recordablePlaces,
  type ClassPlacementsByEntryCodesOutput,
  type ReportClassResultsOutput,
  type ReportPlacementEntryOutput,
  type ReportResultsOutput,
  type TClass,
  type TShowJudge,
} from '@schemas'
import { CurrentShowSummary, SelectShowNotice } from '@/components/current-show'
import { PageHeading } from '@/components/page-heading'
import { useCurrentShow } from '@/context/current-show'
import { useRunMode } from '@/context/run-mode'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { getCategoryBadgeClass, getCategoryLabel } from '@/lib/classColors'
import { cn } from '@/lib/utils'
import {
  useDivisions,
  useReportResults,
  useRecordClassResultsByEntryCodes,
  useShowJudges,
  useStartJudging,
  useUpdateClassFinalization,
  useUpdateClassJudges,
  useExhibitors,
  type DivisionWithClasses,
} from '@/api/hooks'
import { useActorPermissions } from '@/hooks/useActorPermissions'
import { triggerReportPrint } from '@/lib/print'
import watermarkUrl from '@/assets/ahstc-logo.svg?url'
import { ClassTypeahead, type ClassOption } from '@/components/ClassTypeahead'
import { MoveExhibitDialog } from '@/components/judging/MoveExhibitDialog'

const STEPS = [
  { id: 'class', label: 'Choose Class', description: 'Start judging for a show class.' },
  {
    id: 'results',
    label: 'Enter Results',
    description: 'Record placements with Exhibit IDs for the selected class.',
  },
  {
    id: 'judges',
    label: 'Assign Judges',
    description: 'Select the judge(s) who officiated the class.',
  },
  {
    id: 'confirm',
    label: 'Confirm & Print',
    description: 'Review awards, finalize results, and print the class summary.',
  },
] as const

const STEP_SEQUENCE = STEPS.map(step => step.id)
type StepId = (typeof STEPS)[number]['id']
type PlacementKey = (typeof CLASS_PLACEMENT_KEYS)[number]
type SelectedClass = TClass & { divisionName?: string | null }

const PLACEMENT_LABELS: Record<PlacementKey, string> = {
  '1st': '1st place',
  '2nd': '2nd place',
  '3rd': '3rd place',
  '4th': '4th place',
  '5th': '5th place',
  '6th': '6th place',
  '7th': '7th place',
  '8th': '8th place',
  '9th': '9th place',
  '10th': '10th place',
}

const PLACEMENT_RIBBONS: Partial<Record<PlacementKey, { fill: string; stroke?: string }>> = {
  '1st': { fill: '#2563eb' },
  '2nd': { fill: '#dc2626' },
  '3rd': { fill: '#ffffff', stroke: '#111827' },
}

type PlacementInputs = Record<PlacementKey, string>

type PlacementValidation = {
  placements: Record<PlacementKey, string[]>
  response: ClassPlacementsByEntryCodesOutput
}

type EntryCodeErrorDetail = {
  status?: string
  message?: string
  invalidCodes?: string[]
  duplicateCodes?: string[]
  missingCodes?: string[]
  entriesMissingExhibits?: Array<{ entryCode: string; entryId: string }>
  classBreakdown?: Array<{
    entryCode: string
    classId: string | null
    classCode: string | null
    className: string | null
  }>
  tiedPlacements?: string[]
  unavailablePlacements?: string[]
  disqualifiedEntryCodes?: string[]
  showStatus?: string
}

function createEmptyPlacements(): PlacementInputs {
  return CLASS_PLACEMENT_KEYS.reduce((acc, key) => {
    acc[key] = ''
    return acc
  }, {} as PlacementInputs)
}

function splitEntryCodes(input: string): string[] {
  return input
    .split(/[\s,]+/)
    .map(code => code.trim().toUpperCase())
    .filter(Boolean)
}

function toParametersRecord(input: unknown): Record<string, unknown> {
  if (!input) return {}
  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return { ...(parsed as Record<string, unknown>) }
      }
    } catch {
      return {}
    }
  }
  if (typeof input === 'object' && !Array.isArray(input)) {
    return { ...(input as Record<string, unknown>) }
  }
  return {}
}

function extractJudgeAssignments(parameters: unknown): string[] {
  const record = toParametersRecord(parameters)
  const judging = record['judging']
  if (!judging || typeof judging !== 'object' || Array.isArray(judging)) return []
  const assignments = (judging as Record<string, unknown>)['judgeAssignments']
  if (!Array.isArray(assignments)) return []
  return [
    ...new Set(
      assignments.filter((value): value is string => typeof value === 'string' && value.length > 0)
    ),
  ]
}

function buildClassOptions(divisions: DivisionWithClasses[] | undefined): ClassOption[] {
  if (!divisions) return []
  const result: Array<ClassOption & { isFinalized: boolean }> = []
  divisions.forEach(division => {
    const classes = division.classes ?? []
    classes.forEach(showClass => {
      if (!showClass?.id) return
      const code = showClass.code ?? ''
      const name = showClass.name ?? 'Untitled class'
      let statusLabel: string | null = null
      const noAwards = Boolean(showClass.noAwards)
      if (showClass.resultsFinalizedAt && noAwards) statusLabel = 'Finalized — No awards'
      else if (showClass.resultsFinalizedAt) statusLabel = 'Finalized'
      else if (noAwards) statusLabel = 'No awards saved'
      else if (showClass.judgingStartedAt) statusLabel = 'In progress'
      // Historical shows may have category-coded divisions without catalog links.
      const divisionCategory = getCategoryBadgeClass(division.code) ? division.code : undefined
      result.push({
        id: showClass.id,
        code,
        name,
        label: code ? `${code} — ${name}` : name,
        divisionName: division.name ?? null,
        categoryCode: showClass.catalogClass?.categoryCode ?? divisionCategory,
        categoryName:
          showClass.catalogClass?.categoryName ?? (divisionCategory ? division.name : undefined),
        statusLabel,
        isFinalized: Boolean(showClass.resultsFinalizedAt),
      })
    })
  })
  return result.sort(
    (a, b) => Number(a.isFinalized) - Number(b.isFinalized) || a.label.localeCompare(b.label)
  )
}

function findClassOption(
  options: ClassOption[],
  classId: string | null | undefined
): ClassOption | null {
  if (!classId) return null
  return options.find(option => option.id === classId) ?? null
}

function findReportClassResults(
  results: ReportResultsOutput | undefined,
  classId: string | null | undefined
): ReportClassResultsOutput | null {
  if (!results || !classId) return null
  for (const division of results.divisions ?? []) {
    const match = division.classes.find(cls => cls.classId === classId)
    if (match) return match
  }
  return null
}

function hasSavedResults(
  showClass: { noAwards?: boolean | null } | null | undefined,
  results: ReportClassResultsOutput | null
): boolean {
  return (
    Boolean(showClass?.noAwards) ||
    CLASS_PLACEMENT_KEYS.some(place => (results?.placements[place] ?? []).length > 0)
  )
}

function formatJudgeName(assignment: TShowJudge): string {
  return assignment.name?.trim() || 'Unknown judge'
}

// The list carries names only, so judges who share one are numbered in list order.
function judgeChoiceLabels(judges: TShowJudge[]): Map<string, string> {
  const totals = new Map<string, number>()
  for (const judge of judges) {
    const name = formatJudgeName(judge)
    totals.set(name, (totals.get(name) ?? 0) + 1)
  }
  const seen = new Map<string, number>()
  return new Map(
    judges.map(judge => {
      const name = formatJudgeName(judge)
      const total = totals.get(name) ?? 1
      if (total === 1) return [judge.id, name]
      const position = (seen.get(name) ?? 0) + 1
      seen.set(name, position)
      return [judge.id, `${name} (${position} of ${total})`]
    })
  )
}

function buildPlacementsPayload(input: PlacementInputs): {
  placements: Record<PlacementKey, string[]>
  total: number
} {
  const placements = {} as Record<PlacementKey, string[]>
  let total = 0
  for (const place of CLASS_PLACEMENT_KEYS) {
    const codes = splitEntryCodes(input[place] ?? '')
    placements[place] = codes
    total += codes.length
  }
  return { placements, total }
}

function placementsFromValidation(
  validation: PlacementValidation | null
): Record<PlacementKey, ReportPlacementEntryOutput[]> | null {
  if (!validation) return null
  return CLASS_PLACEMENT_KEYS.reduce(
    (acc, place) => {
      const codes = validation.placements[place] ?? []
      acc[place] = codes.map(code => ({
        exhibitId: code,
        exhibitorId: null,
        exhibitorName: null,
        entryTitle: null,
      }))
      return acc
    },
    {} as Record<PlacementKey, ReportPlacementEntryOutput[]>
  )
}

function buildFieldErrors(
  place: PlacementKey,
  detail: EntryCodeErrorDetail | null,
  placements: Record<PlacementKey, string[]>,
  selectedClassId: string | undefined
): string[] {
  if (!detail) return []
  const errors: string[] = []
  const codes = new Set((placements[place] ?? []).map(code => code.toUpperCase()))
  if (detail.invalidCodes?.length) {
    const invalid = detail.invalidCodes
      .map(code => code.toUpperCase())
      .filter(code => codes.has(code))
    if (invalid.length) {
      errors.push(`Invalid Exhibit ID${invalid.length > 1 ? 's' : ''}: ${invalid.join(', ')}`)
    }
  }
  if (detail.duplicateCodes?.length) {
    const duplicates = detail.duplicateCodes
      .map(code => code.toUpperCase())
      .filter(code => codes.has(code))
    if (duplicates.length) {
      errors.push(
        `Duplicate placement${duplicates.length > 1 ? 's' : ''}: ${duplicates.join(', ')}`
      )
    }
  }
  if (detail.missingCodes?.length) {
    const missing = detail.missingCodes
      .map(code => code.toUpperCase())
      .filter(code => codes.has(code))
    if (missing.length) {
      errors.push(
        `Missing Exhibit ID match${missing.length > 1 ? 'es' : ''}: ${missing.join(', ')}`
      )
    }
  }
  if (detail.entriesMissingExhibits?.length) {
    const missingExhibitCodes = detail.entriesMissingExhibits
      .map(entry => entry.entryCode.toUpperCase())
      .filter(code => codes.has(code))
    if (missingExhibitCodes.length) {
      errors.push(`Not checked in: ${missingExhibitCodes.join(', ')}`)
    }
  }
  if (detail.classBreakdown?.length) {
    // When codes span classes the server lists every entry, including those in this class.
    const wrongClass = detail.classBreakdown
      .filter(
        item =>
          codes.has(item.entryCode.toUpperCase()) &&
          !(selectedClassId && item.classId === selectedClassId)
      )
      .map(item => {
        const entryCode = item.entryCode.toUpperCase()
        if (item.classCode && item.className) {
          return `${entryCode} → ${item.classCode} (${item.className})`
        }
        if (item.classCode) {
          return `${entryCode} → ${item.classCode}`
        }
        return `${entryCode} → No class assigned`
      })
    if (wrongClass.length) {
      errors.push(`Wrong class: ${wrongClass.join('; ')}`)
    }
  }
  if (detail.disqualifiedEntryCodes?.length) {
    const disqualified = detail.disqualifiedEntryCodes
      .map(code => code.toUpperCase())
      .filter(code => codes.has(code))
    if (disqualified.length) {
      errors.push(`Disqualified: ${disqualified.join(', ')}`)
    }
  }
  if (codes.size > 0 && detail.unavailablePlacements?.includes(place)) {
    errors.push('This class does not award this place.')
  }
  if (detail.tiedPlacements?.length) {
    const tied = detail.tiedPlacements
      .map(code => code.toUpperCase())
      .filter(code => codes.has(code))
    if (tied.length) {
      errors.push(`Tied placements detected: ${tied.join(', ')}`)
    }
  }
  return errors
}

type StepIndicatorProps = {
  activeStepId: StepId
  furthestIndex: number
  onStepSelect: (stepId: StepId) => void
}

function JudgingStepIndicator({ activeStepId, furthestIndex, onStepSelect }: StepIndicatorProps) {
  return (
    <ol className="flex flex-wrap items-center gap-2" aria-label="Judging steps">
      {STEPS.map((step, index) => {
        const isActive = step.id === activeStepId
        const isComplete = index < furthestIndex
        const isClickable = index <= furthestIndex
        return (
          <li key={step.id}>
            <button
              type="button"
              className={cn(
                'flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition',
                isActive
                  ? 'border-primary bg-primary/10 text-primary'
                  : isComplete
                    ? 'border-muted-foreground/40 bg-muted text-muted-foreground'
                    : 'border-muted-foreground/40 bg-background text-muted-foreground hover:bg-muted'
              )}
              aria-current={isActive ? 'step' : undefined}
              disabled={!isClickable}
              onClick={() => {
                if (isClickable) {
                  onStepSelect(step.id)
                }
              }}
            >
              <span
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-full border text-xs font-semibold',
                  isActive
                    ? 'border-primary bg-primary text-primary-foreground'
                    : isComplete
                      ? 'border-muted-foreground/40 bg-muted-foreground text-background'
                      : 'border-muted-foreground/40 bg-background text-muted-foreground'
                )}
                aria-hidden="true"
              >
                {index + 1}
              </span>
              <span className="font-medium">{step.label}</span>
            </button>
          </li>
        )
      })}
    </ol>
  )
}

export default function JudgingPage() {
  const { currentShow } = useCurrentShow()
  const permissions = useActorPermissions(currentShow?.id)
  const canReadJudges = permissions.can('judging:judges:read')
  const canAssignJudges = permissions.can('judging:judges:write')
  const canStartJudging = permissions.can('judging:state:write')
  const canFinalizeResults = permissions.isLoading || permissions.can('awards:finalize')
  const canMoveExhibits = permissions.can('entries:write')
  // Roles combine per show: one may assign judges without the right to save or finalize
  // results, with or without the right to start a class. It reads the saved results instead.
  const assignsJudgesOnly = !permissions.isLoading && canAssignJudges && !canFinalizeResults
  // Starting is refused without its permission, even on a class already under way.
  const resumesForJudges = assignsJudgesOnly && !canStartJudging
  const { mode } = useRunMode()
  const [searchParams, setSearchParams] = useSearchParams()
  const previousShowIdRef = useRef<string | null>(null)
  const prefilledClassIdsRef = useRef<
    Map<
      string,
      {
        validatedWithLookup: boolean
        userModified: boolean
      }
    >
  >(new Map())

  const [pendingClassId, setPendingClassId] = useState<string>('')
  const [classSearchResetKey, setClassSearchResetKey] = useState(0)
  const [selectedClass, setSelectedClass] = useState<SelectedClass | null>(null)
  const [selectedClassOption, setSelectedClassOption] = useState<ClassOption | null>(null)
  const [placementsInput, setPlacementsInput] = useState<PlacementInputs>(() =>
    createEmptyPlacements()
  )
  const [editedPlaces, setEditedPlaces] = useState<{
    classId: string
    places: PlacementKey[]
  } | null>(null)
  const placementsInputRef = useRef(placementsInput)
  useEffect(() => {
    placementsInputRef.current = placementsInput
  }, [placementsInput])
  const [validation, setValidation] = useState<PlacementValidation | null>(null)
  const [validationErrorDetail, setValidationErrorDetail] = useState<EntryCodeErrorDetail | null>(
    null
  )
  const [validationFormError, setValidationFormError] = useState<string | null>(null)
  const [noAwards, setNoAwards] = useState(false)
  const [judgesSelected, setJudgesSelected] = useState<string[]>([])
  const [isFinalized, setIsFinalized] = useState(false)
  const [furthestIndex, setFurthestIndex] = useState(0)
  const [finalizeNotice, setFinalizeNotice] = useState<string | null>(null)

  const divisionsQuery = useDivisions(currentShow?.id)
  const appliedDivisions = useRef(divisionsQuery.data)
  const classOptions = useMemo(() => buildClassOptions(divisionsQuery.data), [divisionsQuery.data])

  useEffect(() => {
    if (appliedDivisions.current === divisionsQuery.data) return
    appliedDivisions.current = divisionsQuery.data
    const latest = divisionsQuery.data
      ?.flatMap(division => division.classes ?? [])
      .find(showClass => showClass.id === selectedClass?.id)
    if (!latest || !selectedClass) return
    const previousJudges = extractJudgeAssignments(selectedClass.parameters)
    setJudgesSelected(draft =>
      JSON.stringify(draft) === JSON.stringify(previousJudges)
        ? extractJudgeAssignments(latest.parameters)
        : draft
    )
    setSelectedClass(previous => (previous ? { ...previous, ...latest } : previous))
    setIsFinalized(Boolean(latest.resultsFinalizedAt))
    // Preserve an unsaved no-awards choice while updating unedited controls.
    setNoAwards(draft =>
      draft === Boolean(selectedClass.noAwards) ? Boolean(latest.noAwards) : draft
    )
  }, [divisionsQuery.data, selectedClass?.id])

  const judgesQuery = useShowJudges(canReadJudges ? currentShow?.id : undefined)
  // Judges cannot read the entrant list; the server repeats these Exhibit ID checks on save.
  const exhibitorsQuery = useExhibitors(
    { showId: currentShow?.id, limit: 500 },
    { enabled: Boolean(currentShow?.id) && permissions.can('exhibitors:write') }
  )
  const judges = useMemo(
    () => (canReadJudges ? (judgesQuery.data ?? []) : []),
    [canReadJudges, judgesQuery.data]
  )
  const judgesLoading = permissions.isLoading || (canReadJudges && judgesQuery.isLoading)
  const judgesRefused = [401, 403].includes(parseApiErrorStatus(judgesQuery.error) ?? 0)
  // A failed refresh keeps the last list. Only a refusal, or a first read with nothing to show,
  // takes the judges away.
  const judgesError = judgesQuery.isError && (judgesRefused || judgesQuery.data === undefined)

  const startJudging = useStartJudging()
  const recordResults = useRecordClassResultsByEntryCodes()
  const updateClassFinalization = useUpdateClassFinalization()
  const finalizeClassFinalization = useUpdateClassFinalization()
  const updateClassJudges = useUpdateClassJudges()
  const reportResults = useReportResults(currentShow?.id)
  const entryLookup = useMemo(() => {
    const map = new Map<
      string,
      {
        entryId: string
        classId: string | null
        classCode: string | null
        className: string | null
        exhibitId: string | null | undefined
      }
    >()
    exhibitorsQuery.data?.forEach(exhibitor => {
      exhibitor.entries?.forEach(entry => {
        const info = {
          entryId: entry.id,
          classId: entry.class?.id ?? null,
          classCode: entry.class?.code ?? null,
          className: entry.class?.name ?? null,
          exhibitId: entry.exhibitId ?? null,
        }
        const keys: string[] = []
        if (entry.entryCode) keys.push(entry.entryCode.toUpperCase())
        if (entry.exhibitId) keys.push(entry.exhibitId.toUpperCase())
        if (keys.length === 0) return
        keys.forEach(key => {
          map.set(key, info)
        })
      })
    })
    return map
  }, [exhibitorsQuery.data])

  const classLabel = useMemo(() => {
    if (!selectedClass) return null
    const { code, name } = selectedClass
    if (code && name) return `${code} — ${name}`
    return code ?? name ?? null
  }, [selectedClass?.code, selectedClass?.name])

  const classDescription = useMemo(() => {
    if (classLabel) {
      return `Add Exhibit IDs for each placement. Exhibit IDs are randomized per show and must belong to entries checked-in for ${classLabel}.`
    }
    return 'Add Exhibit IDs for each placement. Exhibit IDs are randomized per show and must belong to entries checked-in for this class.'
  }, [classLabel])

  const resultsPending = recordResults.isPending || updateClassFinalization.isPending
  const finalizePending = recordResults.isPending || finalizeClassFinalization.isPending

  useEffect(() => {
    const nextShowId = currentShow?.id ?? null
    if (previousShowIdRef.current === nextShowId) return
    previousShowIdRef.current = nextShowId
    setPendingClassId('')
    setClassSearchResetKey(key => key + 1)
    setSelectedClass(null)
    setSelectedClassOption(null)
    setPlacementsInput(() => createEmptyPlacements())
    setValidation(null)
    setValidationErrorDetail(null)
    setValidationFormError(null)
    setNoAwards(false)
    setJudgesSelected([])
    setIsFinalized(false)
    setFinalizeNotice(null)
    setFurthestIndex(0)
    prefilledClassIdsRef.current.clear()
    setSearchParams({ step: 'class' }, { replace: true })
  }, [currentShow?.id, setSearchParams])

  const requestedStep = (searchParams.get('step') as StepId | null) ?? 'class'
  const requestedIndex = STEP_SEQUENCE.indexOf(requestedStep)
  const normalizedRequestedIndex = requestedIndex >= 0 ? requestedIndex : 0
  const allowedIndex = Math.min(normalizedRequestedIndex, furthestIndex)
  const activeStepId = STEP_SEQUENCE[allowedIndex] as StepId

  useEffect(() => {
    if (normalizedRequestedIndex !== allowedIndex) {
      setSearchParams({ step: activeStepId }, { replace: true })
    }
  }, [normalizedRequestedIndex, allowedIndex, activeStepId, setSearchParams])

  const goToStep = (stepId: StepId) => {
    const index = STEP_SEQUENCE.indexOf(stepId)
    if (index === -1) return
    if (index > furthestIndex) return
    setSearchParams({ step: stepId })
  }

  const advanceToStep = (stepId: StepId) => {
    const index = STEP_SEQUENCE.indexOf(stepId)
    if (index === -1) return
    setFurthestIndex(prev => Math.max(prev, index))
    setSearchParams({ step: stepId })
  }

  const handleClassSelection = (classId: string) => {
    setPendingClassId(classId)
  }

  const handleStartJudging = async () => {
    if (!pendingClassId) {
      toast.error('Select a class to begin judging.')
      return
    }
    const option = findClassOption(classOptions, pendingClassId)
    if (resumesForJudges) {
      const classData = divisionsQuery.data
        ?.flatMap(division => division.classes ?? [])
        .find(showClass => showClass.id === pendingClassId)
      const saved = findReportClassResults(reportResults.data, pendingClassId)
      if (!classData?.judgingStartedAt || !hasSavedResults(classData, saved)) {
        toast.error(
          reportResults.isLoading
            ? 'Class results are still loading. Try again in a moment.'
            : 'Judges can be assigned once judging has started and results are saved for this class.'
        )
        return
      }
      setSelectedClass({ ...classData, divisionName: option?.divisionName ?? null })
      setSelectedClassOption(option)
      setIsFinalized(Boolean(classData.resultsFinalizedAt))
      setNoAwards(Boolean(classData.noAwards))
      setJudgesSelected(extractJudgeAssignments(classData.parameters))
      setPlacementsInput(() => createEmptyPlacements())
      setValidation(null)
      setValidationErrorDetail(null)
      setValidationFormError(null)
      setFinalizeNotice(null)
      setFurthestIndex(2)
      setPendingClassId('')
      setClassSearchResetKey(key => key + 1)
      prefilledClassIdsRef.current.delete(pendingClassId)
      setSearchParams({ step: 'judges' })
      return
    }
    try {
      const classData = await startJudging.mutateAsync({ classId: pendingClassId })
      const nextClass: SelectedClass = {
        ...classData,
        divisionName: option?.divisionName ?? null,
      }
      setSelectedClass(nextClass)
      setSelectedClassOption(option)
      setIsFinalized(Boolean(classData.resultsFinalizedAt))
      setNoAwards(Boolean(classData.noAwards))
      const existingAssignments = extractJudgeAssignments(classData.parameters)
      setJudgesSelected(existingAssignments)
      setPlacementsInput(() => createEmptyPlacements())
      setValidation(null)
      setValidationErrorDetail(null)
      setValidationFormError(null)
      setFinalizeNotice(null)
      setFurthestIndex(1)
      setPendingClassId('')
      setClassSearchResetKey(key => key + 1)
      prefilledClassIdsRef.current.delete(classData.id ?? pendingClassId)
      toast.success(
        option
          ? `Judging started for class ${option.code}`
          : `Judging started for class ${classData.code}`
      )
      await divisionsQuery.refetch()
      advanceToStep('results')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to start judging.')
    }
  }

  useEffect(() => {
    if (!selectedClass?.id) return
    if (noAwards) return
    if (!reportResults.data) return
    const classId = selectedClass.id
    const entryLookupReady = entryLookup.size > 0
    const prefillState = prefilledClassIdsRef.current.get(classId)
    if (prefillState?.userModified && (!entryLookupReady || prefillState.validatedWithLookup))
      return
    const allowOverwrite = !prefillState?.userModified
    const classResults = findReportClassResults(reportResults.data, classId)
    if (!classResults) return

    const mismatched: NonNullable<EntryCodeErrorDetail['classBreakdown']> = []
    const unchecked: NonNullable<EntryCodeErrorDetail['entriesMissingExhibits']> = []

    const nextPlacements = createEmptyPlacements()
    CLASS_PLACEMENT_KEYS.forEach(place => {
      const entries = classResults.placements[place] ?? []
      const validCodes: string[] = []
      entries.forEach(entry => {
        const code = entry.exhibitId?.toUpperCase()
        if (!code) return
        const info = entryLookup.get(code)
        if (!info) {
          if (entryLookupReady) {
            mismatched.push({
              entryCode: code,
              classId: null,
              classCode: null,
              className: null,
            })
          } else {
            validCodes.push(code)
          }
          return
        }
        if (info.classId && info.classId !== classId) {
          mismatched.push({
            entryCode: code,
            classId: info.classId,
            classCode: info.classCode ?? null,
            className: info.className ?? null,
          })
          return
        }
        if (!info.exhibitId) {
          unchecked.push({ entryCode: code, entryId: info.entryId })
          return
        }
        validCodes.push(code)
      })
      nextPlacements[place] = validCodes.join('\n')
    })

    const placementsUpdated =
      allowOverwrite &&
      CLASS_PLACEMENT_KEYS.some(
        place => nextPlacements[place] !== placementsInputRef.current[place]
      )
    if (placementsUpdated) {
      setPlacementsInput(nextPlacements)
      // A validated snapshot belongs to the old placements. Never finalize it
      // after an automatic refresh has shown a different station's results.
      setValidation(null)
      setValidationFormError(null)
      setFinalizeNotice(null)
      // A role that only assigns judges never validates placements, so saved results arriving
      // in the form must not send it back to the results step.
      setFurthestIndex(prev => Math.min(prev, assignsJudgesOnly ? 2 : 1))
    }

    if (mismatched.length > 0 || unchecked.length > 0) {
      setValidation(null)
      setValidationErrorDetail({
        status: 'validation_error',
        message:
          'Existing results include Exhibit IDs that belong to another class or are not checked in. Update the placements before finalizing.',
        classBreakdown: mismatched.length > 0 ? mismatched : undefined,
        entriesMissingExhibits: unchecked.length > 0 ? unchecked : undefined,
      })
      setValidationFormError(null)
      setFinalizeNotice(null)
      toast.error(
        'Existing results include Exhibit IDs that belong to another class or are not checked in. Update the placements before finalizing.'
      )
    } else if (placementsUpdated) {
      setValidationErrorDetail(null)
    }

    const nextPrefillState = {
      validatedWithLookup: entryLookupReady || prefillState?.validatedWithLookup || false,
      userModified: prefillState?.userModified ?? false,
    }

    if (placementsUpdated) {
      nextPrefillState.userModified = false
    }

    prefilledClassIdsRef.current.set(classId, nextPrefillState)
  }, [selectedClass?.id, reportResults.data, entryLookup, noAwards, assignsJudgesOnly])

  const handlePlacementChange = (place: PlacementKey, rawValue: string) => {
    if (noAwards) return
    const value = rawValue.toUpperCase()
    setPlacementsInput(prev => {
      if (prev[place] === value) return prev
      return { ...prev, [place]: value }
    })
    if (selectedClass?.id) {
      const classId = selectedClass.id
      const prior = prefilledClassIdsRef.current.get(classId)
      prefilledClassIdsRef.current.set(classId, {
        validatedWithLookup: prior?.validatedWithLookup ?? false,
        userModified: true,
      })
      setEditedPlaces(prev =>
        prev?.classId !== classId
          ? { classId, places: [place] }
          : prev.places.includes(place)
            ? prev
            : { classId, places: [...prev.places, place] }
      )
    }
    if (validation) {
      setValidation(null)
      setValidationErrorDetail(null)
      setValidationFormError(null)
      setFinalizeNotice(null)
      setFurthestIndex(prev => Math.min(prev, 1))
    }
  }

  const handleToggleNoAwards = (checked: boolean) => {
    if (!selectedClass?.id) {
      setNoAwards(checked)
      return
    }

    if (checked) {
      const hasPlacements = CLASS_PLACEMENT_KEYS.some(
        place => placementsInput[place]?.trim().length > 0
      )
      if (hasPlacements || validation) {
        const confirmed = window.confirm(
          'This will ignore current placements for this finalization. Continue?'
        )
        if (!confirmed) return
      }
      setValidation(null)
      setValidationErrorDetail(null)
      setValidationFormError(null)
      setFinalizeNotice(null)
    } else {
      setFinalizeNotice(null)
    }

    setNoAwards(checked)
    setFurthestIndex(prev => Math.min(prev, 1))
  }

  const handleValidateResults = async () => {
    if (!currentShow?.id) {
      toast.error('Select a show before recording class results.')
      return
    }
    if (!selectedClass?.id) {
      toast.error('Choose a class before recording results.')
      return
    }
    if (noAwards) {
      try {
        const updated = await updateClassFinalization.mutateAsync({
          classId: selectedClass.id,
          showId: currentShow.id,
          payload: { noAwards: true },
        })
        setSelectedClass(prev => {
          if (!prev) return { ...updated }
          return {
            ...prev,
            ...updated,
            divisionName: prev.divisionName,
          }
        })
        setNoAwards(true)
        setIsFinalized(Boolean(updated.resultsFinalizedAt))
        setValidation(null)
        setValidationErrorDetail(null)
        setValidationFormError(null)
        const remainsFinalized = Boolean(updated.resultsFinalizedAt)
        setFinalizeNotice(
          remainsFinalized
            ? 'Class remains finalized without awards.'
            : 'Class marked as having no awards. Finalize them after assigning judges.'
        )
        setFurthestIndex(prev => Math.max(prev, 2))
        await Promise.allSettled([divisionsQuery.refetch(), reportResults.refetch()])
        toast.success(
          remainsFinalized
            ? 'Class remains finalized without awards.'
            : 'Class marked as having no awards. Finalize them after judge assignment.'
        )
        advanceToStep('judges')
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Unable to mark this class as having no awards.'
        toast.error(message)
      }
      return
    }
    const { placements, total } = buildPlacementsPayload(placementsInput)
    if (total === 0) {
      setValidationFormError('Enter at least one Exhibit ID before recording results.')
      setValidation(null)
      setValidationErrorDetail(null)
      setFinalizeNotice(null)
      return
    }
    setValidationFormError(null)
    const mismatched: NonNullable<EntryCodeErrorDetail['classBreakdown']> = []
    const unchecked: NonNullable<EntryCodeErrorDetail['entriesMissingExhibits']> = []

    if (entryLookup.size > 0 && selectedClass?.id) {
      CLASS_PLACEMENT_KEYS.forEach(place => {
        for (const code of placements[place]) {
          const info = entryLookup.get(code)
          if (!info) continue
          if (info.classId && info.classId !== selectedClass.id) {
            mismatched.push({
              entryCode: code,
              classId: info.classId,
              classCode: info.classCode ?? null,
              className: info.className ?? null,
            })
          }
          if (!info.exhibitId) {
            unchecked.push({
              entryCode: code,
              entryId: info.entryId,
            })
          }
        }
      })
    }

    if (mismatched.length > 0) {
      const detail: EntryCodeErrorDetail = {
        status: 'validation_error',
        message: 'Some Exhibit IDs belong to a different class. Remove the mismatched IDs first.',
        classBreakdown: mismatched,
      }
      setValidation(null)
      setValidationErrorDetail(detail)
      setValidationFormError(null)
      setFinalizeNotice(null)
      toast.error(detail.message)
      return
    }

    if (unchecked.length > 0) {
      const detail: EntryCodeErrorDetail = {
        status: 'validation_error',
        message: 'Check in these exhibits before recording results.',
        entriesMissingExhibits: unchecked,
      }
      setValidation(null)
      setValidationErrorDetail(detail)
      setValidationFormError(null)
      setFinalizeNotice(null)
      toast.error(detail.message)
      return
    }

    try {
      // Clearing a saved No awards choice commits with the placements, so a rejected
      // save leaves the class's saved results untouched.
      const response = await recordResults.mutateAsync({
        showId: currentShow.id,
        classId: selectedClass.id,
        finalizeClass: false,
        placements,
        ...(selectedClass.noAwards ? { clearNoAwards: true } : {}),
      })
      // This draft is now committed. Later station updates must be allowed to
      // refresh it again; retaining userModified forever freezes a saved form.
      prefilledClassIdsRef.current.set(selectedClass.id, {
        validatedWithLookup: entryLookup.size > 0,
        userModified: false,
      })
      setValidation({ placements, response })
      setValidationErrorDetail(null)
      setFurthestIndex(prev => Math.max(prev, 2))
      const savedClass = response.class
      const remainsFinalized = savedClass
        ? Boolean(savedClass.resultsFinalizedAt)
        : Boolean(isFinalized || selectedClass.resultsFinalizedAt)
      setSelectedClass(previous =>
        previous && savedClass ? { ...previous, ...savedClass } : previous
      )
      setIsFinalized(remainsFinalized)
      setFinalizeNotice(
        !savedClass
          ? 'Results saved. Refresh class results to confirm status.'
          : remainsFinalized
            ? 'Results saved. Class results remain finalized.'
            : 'Results saved. Finalize them after assigning judges.'
      )
      toast.success(
        !savedClass
          ? 'Results recorded. Refresh class results to confirm status.'
          : remainsFinalized
            ? 'Results recorded. Class results remain finalized.'
            : 'Results recorded. Finalize them after judge assignment.'
      )
      await Promise.allSettled([divisionsQuery.refetch(), reportResults.refetch()])
      advanceToStep('judges')
    } catch (error) {
      const detail = parseApiErrorPayload(error) as EntryCodeErrorDetail | null
      setValidationErrorDetail(detail)
      setFinalizeNotice(null)
      const message =
        detail?.message ??
        (error instanceof Error ? error.message : 'Unable to record class results.')
      toast.error(message)
    }
  }

  const placementValidationErrors = useMemo(() => {
    if (!validationErrorDetail) return null
    const { placements } = buildPlacementsPayload(placementsInput)
    return CLASS_PLACEMENT_KEYS.reduce(
      (acc, place) => {
        acc[place] = buildFieldErrors(place, validationErrorDetail, placements, selectedClass?.id)
        return acc
      },
      {} as Record<PlacementKey, string[]>
    )
  }, [placementsInput, validationErrorDetail, selectedClass?.id])

  const handleToggleJudge = (assignmentId: string) => {
    setJudgesSelected(prev => {
      if (prev.includes(assignmentId)) {
        return prev.filter(id => id !== assignmentId)
      }
      return [...prev, assignmentId]
    })
    setFinalizeNotice(null)
    setFurthestIndex(prev => Math.min(prev, 2))
  }

  const unavailableJudgeIds = useMemo(
    () => judgesSelected.filter(id => !judges.some(judge => judge.id === id)),
    [judges, judgesSelected]
  )
  const handleRemoveUnavailableJudges = () => {
    setJudgesSelected(prev => prev.filter(id => !unavailableJudgeIds.includes(id)))
    setFinalizeNotice(null)
    setFurthestIndex(prev => Math.min(prev, 2))
  }

  const handleSaveJudges = async () => {
    if (!canAssignJudges) return
    if (!currentShow?.id) {
      toast.error('Select a show before assigning judges.')
      return
    }
    if (!selectedClass?.id) {
      toast.error('Choose a class before assigning judges.')
      return
    }
    if (judgesSelected.length === 0) {
      toast.error('Select at least one judge.')
      return
    }
    try {
      const updated = await updateClassJudges.mutateAsync({
        classId: selectedClass.id,
        showId: currentShow.id,
        payload: { judgeAssignmentIds: judgesSelected },
      })
      setSelectedClass(prev => {
        if (!prev) return { ...updated }
        return {
          ...prev,
          ...updated,
          divisionName: prev.divisionName,
        }
      })
      setFurthestIndex(prev => Math.max(prev, 3))
      toast.success('Judge assignment saved.')
      advanceToStep('confirm')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to assign judges.')
    }
  }

  const selectedJudgeAssignments = useMemo(() => {
    const lookup = new Map<string, TShowJudge>()
    for (const assignment of judges) {
      if (assignment?.id) {
        lookup.set(assignment.id, assignment)
      }
    }
    return judgesSelected
      .map(id => lookup.get(id))
      .filter((assignment): assignment is TShowJudge => Boolean(assignment))
  }, [judges, judgesSelected])

  const classResults = useMemo(
    () => findReportClassResults(reportResults.data, selectedClass?.id),
    [reportResults.data, selectedClass?.id]
  )

  // The report carries the count the server enforces; the class and show settings stand in
  // until it loads.
  const placeCount =
    classResults?.placeCount ?? classPlaceCount(selectedClass ?? {}, currentShow ?? {})
  // A place holding saved or typed results stays on the form when the class awards fewer, and
  // so does one edited for this class, so clearing it never removes the field being typed in.
  const formPlaces = useMemo(() => {
    const edited =
      editedPlaces && editedPlaces.classId === selectedClass?.id ? editedPlaces.places : []
    return recordablePlaces(
      placeCount,
      CLASS_PLACEMENT_KEYS.filter(
        place =>
          edited.includes(place) ||
          placementsInput[place].trim() !== '' ||
          (classResults?.placements[place] ?? []).length > 0
      )
    )
  }, [placeCount, editedPlaces, selectedClass?.id, placementsInput, classResults])

  const summaryPlacements = useMemo(() => {
    if (classResults) {
      return CLASS_PLACEMENT_KEYS.reduce(
        (acc, place) => {
          acc[place] = classResults.placements[place] ?? []
          return acc
        },
        {} as Record<PlacementKey, ReportPlacementEntryOutput[]>
      )
    }
    return placementsFromValidation(validation)
  }, [classResults, validation])

  const handleFinalizeResults = async () => {
    if (!currentShow?.id) {
      toast.error('Select a show before finalizing results.')
      return
    }
    if (!selectedClass?.id) {
      toast.error('Choose a class before finalizing results.')
      return
    }
    if (noAwards) {
      // Finalizing without awards permanently retires the class's saved placements.
      const classLabel = selectedClass.name ?? selectedClass.code ?? 'this class'
      if (
        !window.confirm(
          `Finalize ${classLabel} without awards? Its saved placements will be permanently removed.`
        )
      )
        return
      try {
        const updated = await finalizeClassFinalization.mutateAsync({
          classId: selectedClass.id,
          showId: currentShow.id,
          payload: { noAwards: true, finalized: true },
        })
        setSelectedClass(prev => {
          if (!prev) return { ...updated }
          return {
            ...prev,
            ...updated,
            divisionName: prev.divisionName,
          }
        })
        setNoAwards(true)
        setIsFinalized(true)
        setFinalizeNotice('Class finalized without awards. Ready to print.')
        await Promise.allSettled([divisionsQuery.refetch(), reportResults.refetch()])
        toast.success('Class finalized without awards.')
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Unable to finalize this class without awards.'
        toast.error(message)
      }
      return
    }
    if (!validation) {
      toast.error('Validate class results before finalizing.')
      return
    }
    try {
      const response = await recordResults.mutateAsync({
        showId: currentShow.id,
        classId: selectedClass.id,
        finalizeClass: true,
        placements: validation.placements,
        ...(selectedClass.noAwards ? { clearNoAwards: true } : {}),
      })
      setValidation(prev => (prev ? { ...prev, response } : prev))
      setIsFinalized(true)
      setFinalizeNotice('Class results finalized. Ready to print.')
      setNoAwards(false)
      setSelectedClass(prev => (prev && response.class ? { ...prev, ...response.class } : prev))
      await Promise.allSettled([divisionsQuery.refetch(), reportResults.refetch()])
      toast.success('Class results finalized.')
    } catch (error) {
      const detail = parseApiErrorPayload(error) as EntryCodeErrorDetail | null
      const message =
        detail?.message ??
        (error instanceof Error ? error.message : 'Unable to finalize class results.')
      toast.error(message)
    }
  }

  const handlePrintSummary = () => {
    triggerReportPrint()
  }

  const classStatusLabel = useMemo(() => {
    if (isFinalized || selectedClass?.resultsFinalizedAt) {
      return selectedClass?.noAwards ? 'Finalized — No awards' : 'Finalized'
    }
    if (selectedClass?.noAwards) return 'No awards saved'
    if (selectedClass?.judgingStartedAt) return 'In progress'
    return null
  }, [
    isFinalized,
    selectedClass?.resultsFinalizedAt,
    selectedClass?.judgingStartedAt,
    selectedClass?.noAwards,
  ])

  if (!currentShow) {
    return (
      <SelectShowNotice description="Choose a show to manage judging workflows and record scores." />
    )
  }

  if (mode === 'run' && currentShow.status !== 'judging') {
    return (
      <Card className="border-dashed border-amber-300 bg-amber-50">
        <CardHeader>
          <CardTitle>Switch this show to the Judging phase</CardTitle>
          <CardDescription>
            Judges can only record scores when the show status is <code>Judging</code>. Advance the
            show via the setup workflow once intake is complete.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button asChild>
            <Link to={`/${currentShow.slug}/setup`}>Preview setup tasks</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/shows">Manage shows</Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <header className="space-y-2 print:hidden">
        <PageHeading>
          Judging
          {classLabel ? (
            <span className="ml-2 text-base font-semibold text-muted-foreground">{classLabel}</span>
          ) : null}
        </PageHeading>
        <p className="text-muted-foreground">
          Follow the guided flow to select a class, record placements with Exhibit IDs, assign
          judge(s), and print the official judging summary.
        </p>
      </header>

      {mode !== 'run' && (
        <div className="print:hidden">
          <CurrentShowSummary show={currentShow} />
        </div>
      )}

      <div className="print:hidden">
        <JudgingStepIndicator
          activeStepId={activeStepId}
          furthestIndex={furthestIndex}
          onStepSelect={goToStep}
        />
      </div>

      <div>
        {activeStepId === 'class' && (
          <ClassSelectionCard
            key={classSearchResetKey}
            showName={currentShow.name ?? 'Show'}
            options={classOptions}
            selectedClass={selectedClass}
            selectedOption={selectedClassOption}
            pendingClassId={pendingClassId}
            onSelectClass={handleClassSelection}
            onStart={handleStartJudging}
            resumesForJudges={resumesForJudges}
            isLoading={divisionsQuery.isLoading}
            hasError={divisionsQuery.isError}
            isPending={startJudging.isPending}
            moveAction={
              canMoveExhibits && currentShow.id && currentShow.status === 'judging' ? (
                <MoveExhibitDialog showId={currentShow.id} classOptions={classOptions} />
              ) : null
            }
          />
        )}

        {activeStepId === 'results' && (
          <ResultsEntryCard
            selectedClass={selectedClass}
            classLabel={classLabel}
            classDescription={classDescription}
            placeCount={placeCount}
            places={formPlaces}
            placementsInput={placementsInput}
            onChangePlacement={handlePlacementChange}
            onBack={() => goToStep('class')}
            onValidate={handleValidateResults}
            isPending={resultsPending}
            validationError={validationErrorDetail}
            placementErrors={placementValidationErrors}
            formError={validationFormError}
            noAwards={noAwards}
            placementsDisabled={noAwards || assignsJudgesOnly}
            onToggleNoAwards={handleToggleNoAwards}
            toggleDisabled={resultsPending || finalizePending || assignsJudgesOnly}
            onContinue={assignsJudgesOnly ? () => advanceToStep('judges') : undefined}
            resultsSaved={hasSavedResults(selectedClass, classResults)}
          />
        )}

        {activeStepId === 'judges' && (
          <JudgeAssignmentCard
            selectedClass={selectedClass}
            judges={judges}
            selectedJudgeIds={judgesSelected}
            onToggleJudge={handleToggleJudge}
            onBack={() => goToStep('results')}
            onSave={handleSaveJudges}
            isPending={updateClassJudges.isPending}
            unavailableCount={unavailableJudgeIds.length}
            onRemoveUnavailable={handleRemoveUnavailableJudges}
            onRetry={() => void judgesQuery.refetch()}
            staffLoading={judgesLoading}
            staffError={judgesError}
            canReadJudges={canReadJudges}
            canAssignJudges={canAssignJudges}
            canManageStaff={permissions.can('staff:write')}
          />
        )}

        {activeStepId === 'confirm' && (
          <ConfirmAndPrintCard
            selectedClass={selectedClass}
            selectedOption={selectedClassOption}
            classStatusLabel={classStatusLabel}
            placements={summaryPlacements}
            judges={selectedJudgeAssignments}
            judgesLoading={judgesLoading}
            judgesError={judgesError}
            judgesUnavailable={unavailableJudgeIds.length > 0}
            onRetryJudges={() => void judgesQuery.refetch()}
            canFinalize={canFinalizeResults}
            isFinalized={isFinalized || Boolean(selectedClass?.resultsFinalizedAt)}
            noAwards={Boolean(selectedClass?.noAwards)}
            onBack={() => goToStep('judges')}
            onResetToClass={() => {
              setSelectedClass(null)
              setSelectedClassOption(null)
              setPlacementsInput(() => createEmptyPlacements())
              setValidation(null)
              setValidationErrorDetail(null)
              setValidationFormError(null)
              setFinalizeNotice(null)
              setJudgesSelected([])
              advanceToStep('class')
            }}
            onFinalize={handleFinalizeResults}
            finalizePending={finalizePending}
            onPrint={handlePrintSummary}
            reportLoading={reportResults.isLoading}
            finalizeNotice={finalizeNotice}
          />
        )}
      </div>
    </div>
  )
}

type ClassSelectionCardProps = {
  showName: string
  options: ClassOption[]
  selectedClass: SelectedClass | null
  selectedOption: ClassOption | null
  pendingClassId: string
  onSelectClass: (classId: string) => void
  onStart: () => void | Promise<void>
  resumesForJudges: boolean
  isLoading: boolean
  hasError: boolean
  isPending: boolean
  moveAction?: ReactNode
}

function ClassSelectionCard({
  showName,
  options,
  selectedClass,
  selectedOption,
  pendingClassId,
  onSelectClass,
  onStart,
  resumesForJudges,
  isLoading,
  hasError,
  isPending,
  moveAction,
}: ClassSelectionCardProps) {
  const pendingOption = findClassOption(options, pendingClassId)
  return (
    <Card className="@container/choose-class">
      <CardHeader>
        <CardTitle>Choose Class</CardTitle>
        <CardDescription>
          Select the class you are judging for {showName}. Starting judging stamps the class with a
          judging start time and unlocks the guided workflow for results entry.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <ClassTypeahead
            showCategoryFilters
            id="judging-class-typeahead"
            value={pendingClassId}
            onChange={onSelectClass}
            options={options}
            isLoading={isLoading}
            disabled={isPending}
            placeholder="Search by class code or name…"
          />
          {pendingOption?.categoryCode ? (
            <div
              className="flex flex-wrap items-center gap-2 text-sm"
              data-testid="selected-class-category"
            >
              <Badge
                variant="secondary"
                className={getCategoryBadgeClass(pendingOption.categoryCode)}
              >
                {getCategoryLabel(pendingOption.categoryCode, pendingOption.categoryName)}
              </Badge>
              <span className="text-muted-foreground">{pendingOption.divisionName}</span>
            </div>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Exhibit IDs are unique per show. Verify you are judging the correct class before
            proceeding.
          </p>
          {resumesForJudges ? (
            <p className="text-xs text-muted-foreground">
              Your role assigns judges. Open a class once an Organizer, Presiding Judge, Lead
              Secretary, or Administrator has started it and saved its results.
            </p>
          ) : null}
          {hasError ? (
            <p className="text-sm text-destructive">
              Unable to load classes for this show. Try refreshing or confirm you have access.
            </p>
          ) : null}
        </div>

        {selectedClass ? (
          <div className="rounded-lg border bg-muted/30 p-4 text-sm">
            <p className="font-semibold text-foreground">
              {selectedClass.code} — {selectedClass.name}
            </p>
            <p className="text-muted-foreground">
              {selectedClass.divisionName ?? selectedOption?.divisionName ?? 'Unassigned division'}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedClass.judgingStartedAt ? (
                <Badge variant="secondary">Judging started</Badge>
              ) : null}
              {selectedClass.noAwards ? <Badge variant="outline">No awards saved</Badge> : null}
              {selectedClass.resultsFinalizedAt ? <Badge variant="outline">Finalized</Badge> : null}
            </div>
          </div>
        ) : null}
      </CardContent>
      <CardFooter className="flex-col-reverse items-stretch gap-3 @md/choose-class:flex-row @md/choose-class:items-center @md/choose-class:justify-end">
        {moveAction ? <div className="grid @md/choose-class:mr-auto">{moveAction}</div> : null}
        <Button type="button" onClick={onStart} disabled={!pendingClassId || isPending}>
          {isPending
            ? 'Starting…'
            : selectedClass
              ? 'Switch Class'
              : resumesForJudges
                ? 'Open Class'
                : 'Start Judging'}
        </Button>
      </CardFooter>
    </Card>
  )
}

type ResultsEntryCardProps = {
  selectedClass: SelectedClass | null
  classLabel: string | null
  classDescription: string
  /** Places the class awards. */
  placeCount: number
  /** Places on the form: the awarded places and any later place holding results. */
  places: readonly PlacementKey[]
  placementsInput: PlacementInputs
  onChangePlacement: (place: PlacementKey, value: string) => void
  onBack: () => void
  onValidate: () => void
  isPending: boolean
  validationError: EntryCodeErrorDetail | null
  placementErrors: Record<PlacementKey, string[]> | null
  formError: string | null
  noAwards: boolean
  placementsDisabled: boolean
  onToggleNoAwards: (checked: boolean) => void
  toggleDisabled: boolean
  /** Set for a role that only assigns judges: the saved results are shown, not edited. */
  onContinue?: () => void
  resultsSaved: boolean
}

function ResultsEntryCard({
  selectedClass,
  classLabel,
  classDescription,
  placeCount,
  places,
  placementsInput,
  onChangePlacement,
  onBack,
  onValidate,
  isPending,
  validationError,
  placementErrors,
  formError,
  noAwards,
  placementsDisabled,
  onToggleNoAwards,
  toggleDisabled,
  onContinue,
  resultsSaved,
}: ResultsEntryCardProps) {
  const noAwardsId = useId()
  if (!selectedClass) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Select a class first</CardTitle>
          <CardDescription>
            Choose a class in step one to enter placements and validate Exhibit IDs.
          </CardDescription>
        </CardHeader>
        <CardFooter className="flex justify-start">
          <Button type="button" variant="outline" onClick={onBack}>
            Back to class selection
          </Button>
        </CardFooter>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{classLabel ? `Enter Results — ${classLabel}` : 'Enter Results'}</CardTitle>
        <CardDescription>{classDescription}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {onContinue ? (
          <p className="text-sm text-muted-foreground">
            {resultsSaved
              ? 'These are the saved results. Changing them requires an Organizer, Presiding Judge, Lead Secretary, or Administrator.'
              : 'No results are saved for this class yet. Saving them requires an Organizer, Presiding Judge, Lead Secretary, or Administrator; judges can be assigned afterwards.'}
          </p>
        ) : null}
        {validationError?.message ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive-foreground">
            {validationError.message}
          </div>
        ) : null}

        {formError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive-foreground">
            {formError}
          </div>
        ) : null}

        {places.length > placeCount ? (
          <p className="text-sm text-muted-foreground">
            This class awards {formatPlaceCount(placeCount)}. Later places are shown because they
            already hold results.
          </p>
        ) : null}

        <div className="grid gap-6 md:grid-cols-2">
          {[places.slice(0, 3), places.slice(3)]
            .filter(columnPlaces => columnPlaces.length > 0)
            .map((columnPlaces, columnIndex) => (
              <div key={columnIndex} className="space-y-6">
                {columnPlaces.map(place => {
                  const errors = placementErrors?.[place] ?? []
                  const fieldId = `placement-${place}`
                  return (
                    <div key={place} className="space-y-2">
                      <label
                        htmlFor={fieldId}
                        className="flex items-center gap-2 text-sm font-medium text-foreground"
                      >
                        {PLACEMENT_RIBBONS[place] ? (
                          <RibbonIcon
                            className="h-5 w-5"
                            color={PLACEMENT_RIBBONS[place]!.fill}
                            stroke={PLACEMENT_RIBBONS[place]!.stroke}
                          />
                        ) : null}
                        {PLACEMENT_LABELS[place]}
                      </label>
                      <Input
                        id={fieldId}
                        value={placementsInput[place]}
                        onChange={event => onChangePlacement(place, event.target.value)}
                        className="font-mono text-sm uppercase"
                        autoComplete="off"
                        disabled={isPending || placementsDisabled}
                      />
                      {errors.length > 0 ? (
                        <ul className="list-disc space-y-1 pl-5 text-sm text-destructive">
                          {errors.map(error => (
                            <li key={error}>{error}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            ))}
        </div>
        <div className="rounded-md border border-border/60 bg-muted/20 p-4">
          <div className="flex items-start gap-3">
            <input
              id={noAwardsId}
              type="checkbox"
              className="mt-1"
              checked={noAwards}
              onChange={event => onToggleNoAwards(event.target.checked)}
              disabled={toggleDisabled}
            />
            <div className="space-y-1">
              <label
                htmlFor={noAwardsId}
                className="block text-sm font-medium leading-6 text-foreground"
              >
                No awards were given for this class
              </label>
              <p className="text-sm text-muted-foreground">
                Selecting this option disables placement entry and lets you continue without Exhibit
                IDs.
              </p>
            </div>
          </div>
          {noAwards ? (
            <p className="mt-2 text-sm text-muted-foreground" aria-live="polite">
              You can finalize without placements when this option is selected.
            </p>
          ) : null}
        </div>
      </CardContent>
      <CardFooter className="flex flex-wrap justify-between gap-3">
        <Button type="button" variant="outline" onClick={onBack} disabled={isPending}>
          Back
        </Button>
        {onContinue ? (
          <Button type="button" onClick={onContinue} disabled={!resultsSaved}>
            Continue
          </Button>
        ) : (
          <Button type="button" onClick={onValidate} disabled={isPending}>
            {isPending
              ? noAwards
                ? 'Saving…'
                : 'Validating…'
              : noAwards
                ? 'Save No Awards & Continue'
                : 'Validate & Continue'}
          </Button>
        )}
      </CardFooter>
    </Card>
  )
}

type RibbonIconProps = {
  color: string
  stroke?: string
  className?: string
}

function RibbonIcon({ color, stroke, className }: RibbonIconProps) {
  return (
    <svg
      viewBox="0 0 500 500"
      className={cn('shrink-0', className)}
      aria-hidden="true"
      focusable="false"
    >
      <path fill="none" d="M0 0h500v500H0z" />
      <path
        d="M254.291 68.6c-79.803 0-144.798 63.623-144.798 143.427 0 79.803 63.623 143.426 143.427 143.426 79.803 0 143.426-63.623 143.426-143.426C397.717 132.223 334.094 68.6 254.291 68.6"
        fill={color}
        stroke={stroke}
        strokeWidth={stroke ? 12 : undefined}
      />
      <path
        d="M394.701 326.11h6.033c10.421 1.371 14.809-4.388 10.421-14.809-4.388-8.776 0-17.825 10.421-19.197 10.421 0 13.438-7.404 7.405-16.18-7.405-8.775-4.388-17.825 6.033-20.568 10.421-3.016 11.792-10.421 4.388-17.825-8.776-7.405-7.405-16.18 1.371-20.568s8.775-13.438 0-17.825c-8.776-4.388-10.421-14.809-1.371-20.568 8.775-7.405 6.033-14.809-4.388-17.826-10.421-3.016-13.438-11.792-6.033-20.568 7.404-8.775 3.016-16.18-7.405-16.18s-14.809-8.775-10.421-17.825 0-16.18-10.421-14.81c-10.421 1.372-16.18-6.032-14.809-16.18 1.371-10.42-3.016-14.808-13.437-10.42-10.422 4.388-17.826-1.371-17.826-11.792 0-10.422-6.033-14.81-14.809-8.776-8.775 6.033-17.825 1.371-19.196-8.776-1.372-10.42-8.776-13.437-16.181-6.033-7.404 7.404-16.18 6.033-20.567-4.388s-11.793-10.42-17.826-1.37c-3.016 4.387-7.404 7.403-11.792 6.032-4.388 0-8.776-1.37-11.792-6.033-6.034-8.776-13.438-7.404-17.826 1.371s-13.438 11.793-20.568 4.388c-7.404-8.776-16.18-6.033-17.825 4.388-1.371 10.421-10.421 14.809-19.197 8.776-8.776-6.034-16.18-1.372-14.809 8.775 0 10.421-7.404 16.18-17.825 11.792-10.421-3.016-16.18 3.017-13.438 13.438 3.017 10.421-4.388 17.826-14.809 16.18-10.42-1.371-14.809 4.388-10.42 14.809 4.387 8.776 0 17.825-10.422 17.825-10.42 0-13.437 7.405-7.404 16.181 7.404 7.404 4.388 16.18-6.034 19.196-10.42 3.017-11.792 11.792-4.387 17.826 7.404 6.033 7.404 16.18-3.017 20.568-8.776 4.387-8.776 13.437 0 17.825s10.421 14.809 3.017 20.568c-8.776 7.404-6.034 14.809 4.387 17.825 10.422 3.017 13.438 11.793 6.034 20.568s-3.017 16.18 7.404 16.18 14.809 8.776 10.421 19.197c-4.388 8.776 0 16.18 10.421 14.809h6.033L53 435.531h61.978l30.989 47.443 59.235-97.629c6.033-3.016 13.438 0 16.18 7.405 4.388 10.421 11.792 10.421 17.826 1.371 3.016-4.388 7.404-7.404 11.792-6.033 4.388 0 8.776 1.371 11.792 6.033 6.033 8.776 13.438 7.404 17.826-1.371 3.016-8.776 10.421-10.421 16.18-7.405l59.235 97.629 30.989-47.443H449zm-64.995 22.213-3.016 1.371c-22.213 11.793-47.443 17.826-74.045 17.826-26.601 0-51.831-7.405-74.044-17.826l-3.016-1.371c-48.815-26.601-81.45-78.432-81.45-136.022 0-85.837 71.028-155.219 156.865-155.219s156.864 69.382 156.864 155.219c3.017 57.59-29.343 109.421-78.158 136.022"
        fill={color}
        stroke={stroke}
        strokeWidth={stroke ? 12 : undefined}
      />
    </svg>
  )
}

type JudgeAssignmentCardProps = {
  selectedClass: SelectedClass | null
  judges: TShowJudge[]
  selectedJudgeIds: string[]
  onToggleJudge: (assignmentId: string) => void
  onBack: () => void
  onSave: () => void
  isPending: boolean
  unavailableCount: number
  onRemoveUnavailable: () => void
  onRetry: () => void
  staffLoading: boolean
  staffError: boolean
  canReadJudges: boolean
  canAssignJudges: boolean
  canManageStaff: boolean
}

function JudgeAssignmentCard({
  selectedClass,
  judges,
  selectedJudgeIds,
  onToggleJudge,
  onBack,
  onSave,
  isPending,
  unavailableCount,
  onRemoveUnavailable,
  onRetry,
  staffLoading,
  staffError,
  canReadJudges,
  canAssignJudges,
  canManageStaff,
}: JudgeAssignmentCardProps) {
  const { currentShow } = useCurrentShow()
  if (!selectedClass) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Select a class first</CardTitle>
          <CardDescription>Choose a class before assigning judges.</CardDescription>
        </CardHeader>
        <CardFooter className="flex justify-start">
          <Button type="button" variant="outline" onClick={onBack}>
            Back to class selection
          </Button>
        </CardFooter>
      </Card>
    )
  }

  const judgeAssignments = judges.filter(assignment => Boolean(assignment?.id))
  const hasJudges = judgeAssignments.length > 0
  const labels = judgeChoiceLabels(judgeAssignments)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Assign Judges</CardTitle>
        <CardDescription>
          Select the judge(s) who officiated {selectedClass.code} — {selectedClass.name}. Save to
          continue to confirmation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-lg border bg-muted/30 p-4 text-sm">
          <p className="font-semibold text-foreground">
            {selectedClass.code} — {selectedClass.name}
          </p>
          <p className="text-muted-foreground">
            {selectedClass.divisionName ?? 'Unassigned division'}
          </p>
        </div>

        {staffLoading ? (
          <p className="text-sm text-muted-foreground">Loading judge assignments…</p>
        ) : null}

        {!staffLoading && !canReadJudges ? (
          <p className="text-sm text-muted-foreground">
            Assigning class judges requires an Organizer, Lead Secretary, Presiding Judge,
            Secretary, or Administrator for this show.
          </p>
        ) : staffError ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive-foreground">
            <p className="font-medium">Unable to load judges for this show.</p>
            <p className="mt-1">
              Check your connection to the show server and your permissions for this show.
            </p>
            <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onRetry}>
              Retry
            </Button>
          </div>
        ) : hasJudges ? (
          <ul className="space-y-3">
            {judgeAssignments.map(assignment => {
              const assignmentId = assignment.id as string
              const checked = selectedJudgeIds.includes(assignmentId)
              return (
                <li
                  key={assignmentId}
                  className="flex items-start gap-3 rounded-md border border-border/60 bg-background p-3"
                >
                  <input
                    id={`judge-${assignmentId}`}
                    type="checkbox"
                    className="mt-1"
                    checked={checked}
                    onChange={() => onToggleJudge(assignmentId)}
                    disabled={isPending || !canAssignJudges}
                  />
                  <div className="space-y-1">
                    <label
                      htmlFor={`judge-${assignmentId}`}
                      className="block text-sm font-medium text-foreground"
                    >
                      {labels.get(assignmentId)}
                    </label>
                  </div>
                </li>
              )
            })}
          </ul>
        ) : staffLoading ? null : (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="font-medium">No judges assigned to this show yet.</p>
            <p className="mt-1">
              {canManageStaff
                ? 'Add judges in Setup → Staff, then return here to assign them to the class.'
                : 'Ask an Organizer, Lead Secretary, or Administrator to add judges in Setup → Staff, then return here to assign them to the class.'}
            </p>
            <div className="mt-3 flex gap-2">
              {canManageStaff && (
                <Button asChild variant="outline" size="sm">
                  <Link to={currentShow ? `/${currentShow.slug}/setup?section=staff` : '/shows'}>
                    Open Staff setup
                  </Link>
                </Button>
              )}
            </div>
          </div>
        )}
        {canReadJudges && !staffLoading && !staffError && unavailableCount > 0 && (
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>
              Some saved judges are no longer on this show's judge list. Remove them before saving.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isPending || !canAssignJudges}
              onClick={onRemoveUnavailable}
            >
              Remove unavailable judges
            </Button>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-wrap justify-between gap-3">
        <Button type="button" variant="outline" onClick={onBack} disabled={isPending}>
          Back
        </Button>
        <Button
          type="button"
          onClick={onSave}
          disabled={
            isPending ||
            !canAssignJudges ||
            staffLoading ||
            staffError ||
            selectedJudgeIds.length === 0 ||
            !hasJudges ||
            unavailableCount > 0
          }
        >
          {isPending ? 'Saving…' : 'Save Judges'}
        </Button>
      </CardFooter>
    </Card>
  )
}

type ConfirmAndPrintCardProps = {
  selectedClass: SelectedClass | null
  selectedOption: ClassOption | null
  classStatusLabel: string | null
  placements: Record<PlacementKey, ReportPlacementEntryOutput[]> | null
  judges: TShowJudge[]
  judgesLoading: boolean
  judgesError: boolean
  judgesUnavailable: boolean
  onRetryJudges: () => void
  canFinalize: boolean
  isFinalized: boolean
  noAwards: boolean
  onBack: () => void
  onResetToClass: () => void
  onFinalize: () => void
  finalizePending: boolean
  onPrint: () => void
  reportLoading: boolean
  finalizeNotice: string | null
}

function ConfirmAndPrintCard({
  selectedClass,
  selectedOption,
  classStatusLabel,
  placements,
  judges,
  judgesLoading,
  judgesError,
  judgesUnavailable,
  onRetryJudges,
  canFinalize,
  isFinalized,
  noAwards,
  onBack,
  onResetToClass,
  onFinalize,
  finalizePending,
  onPrint,
  reportLoading,
  finalizeNotice,
}: ConfirmAndPrintCardProps) {
  if (!selectedClass) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Select a class first</CardTitle>
          <CardDescription>
            Complete the earlier steps to review the confirmation summary.
          </CardDescription>
        </CardHeader>
        <CardFooter className="flex justify-start">
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
        </CardFooter>
      </Card>
    )
  }

  const canPrint =
    !noAwards &&
    placements !== null &&
    CLASS_PLACEMENT_KEYS.some(place => (placements?.[place] ?? []).length > 0)
  const finalizeDisabled =
    finalizePending ||
    isFinalized ||
    !canFinalize ||
    judges.length === 0 ||
    judgesLoading ||
    judgesError ||
    judgesUnavailable
  const printDisabled =
    !isFinalized || !canPrint || judgesLoading || judgesError || judgesUnavailable

  return (
    <Card className="space-y-0">
      <CardHeader className="print:hidden">
        <CardTitle>Confirm & Print</CardTitle>
        <CardDescription>
          Review placements, confirm judge assignments, finalize the class, then print the summary
          card for records and labels.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {finalizeNotice ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100 print:hidden">
            {finalizeNotice}
          </div>
        ) : null}

        <div className="relative overflow-hidden rounded-lg border border-border/70 bg-background text-foreground shadow-sm print:bg-white print:text-black print:shadow-none print:[&_*]:text-black print:[&_*]:border-gray-300">
          <div
            className="pointer-events-none absolute inset-0 opacity-5 print:opacity-10"
            style={{
              backgroundImage: `url(${watermarkUrl})`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: '70%',
            }}
            aria-hidden="true"
          />
          <div className="relative space-y-6 p-6 print:p-10">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground">Class</p>
                <p className="text-xl font-semibold text-foreground">
                  {selectedClass.code} — {selectedClass.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedClass.divisionName ??
                    selectedOption?.divisionName ??
                    'Unassigned division'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {classStatusLabel ? <Badge variant="outline">{classStatusLabel}</Badge> : null}
                {isFinalized ? <Badge variant="secondary">Ready to print</Badge> : null}
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-foreground">Judge(s)</p>
              {judgesLoading ? (
                <p className="mt-2 text-sm text-muted-foreground">Loading judge names…</p>
              ) : judgesError ? (
                <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-destructive">
                  <p>Unable to load judges for this show.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="print:hidden"
                    onClick={onRetryJudges}
                  >
                    Retry
                  </Button>
                </div>
              ) : judgesUnavailable ? (
                <p className="mt-2 text-sm text-destructive">
                  Some saved judges are no longer on this show's judge list. Return to Assign Judges
                  to update them.
                </p>
              ) : judges.length > 0 ? (
                <ul className="mt-2 space-y-1 text-sm">
                  {judges.map(assignment => (
                    <li key={assignment.id ?? formatJudgeName(assignment)}>
                      {formatJudgeName(assignment)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-destructive">
                  Judge assignment missing. Return to the previous step to add judge(s).
                </p>
              )}
            </div>

            <div>
              <p className="text-sm font-medium text-foreground">Placements</p>
              {noAwards ? (
                <p className="mt-2 text-sm text-muted-foreground" aria-live="polite">
                  No awards were given for this class. Finalization will record this status in the
                  reports and dashboard.
                </p>
              ) : reportLoading && !placements ? (
                <p className="mt-2 text-sm text-muted-foreground">Loading results…</p>
              ) : placements ? (
                <div className="mt-3 grid gap-4 md:grid-cols-2">
                  {CLASS_PLACEMENT_KEYS.map(place => {
                    const entries = placements[place] ?? []
                    if (entries.length === 0) return null
                    return (
                      <div
                        key={place}
                        className="rounded-md border border-border bg-card p-3 text-sm text-card-foreground shadow-sm print:bg-white print:shadow-none"
                      >
                        <p className="font-semibold text-foreground">{PLACEMENT_LABELS[place]}</p>
                        <ul className="mt-2 space-y-1">
                          {entries.map(entry => (
                            <li
                              key={`${place}-${entry.exhibitId}`}
                              className="text-sm leading-relaxed"
                            >
                              <span className="font-mono text-sm text-foreground">
                                {entry.exhibitId ?? '—'}
                              </span>{' '}
                              —{' '}
                              <span className="text-muted-foreground">
                                {entry.exhibitorName ?? 'Unknown exhibitor'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">
                  Validate placements to preview the class summary before finalizing.
                </p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
      {!canFinalize && !isFinalized ? (
        <p className="px-6 text-sm text-muted-foreground print:hidden">
          Finalizing results requires an Organizer, Presiding Judge, Lead Secretary, or
          Administrator.
        </p>
      ) : null}
      <CardFooter className="flex flex-wrap justify-between gap-3 print:hidden">
        <Button type="button" variant="outline" onClick={onBack} disabled={finalizePending}>
          Back
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="ghost" onClick={onResetToClass} disabled={finalizePending}>
            Choose Another Class
          </Button>
          <Button type="button" onClick={onFinalize} disabled={finalizeDisabled}>
            {finalizePending ? 'Finalizing…' : isFinalized ? 'Finalized' : 'Finalize Results'}
          </Button>
          <Button type="button" variant="outline" onClick={onPrint} disabled={printDisabled}>
            Print Summary
          </Button>
        </div>
      </CardFooter>
    </Card>
  )
}
